import React, { useEffect } from 'react';
import * as Cesium from "cesium"; // Import all of Cesium as a namespace
import {
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  defined,
  HeadingPitchRange, // Added for flyTo offset
  Math as CesiumMath, // Added for flyTo offset (using alias to avoid conflict)
  BoundingSphere, // Added for flyToBoundingSphere
} from "cesium";
import { useCesiumViewer } from '../../../context/CesiumViewerContext'; // Corrected path

const InteractionHandler = ({
  // Props:
  assets, // Current assets for checking properties
  // State setters from MapViewPage
  setTooltipVisible,
  setTooltipContent,
  setTooltipPosition,
  setSelectedAsset,
  setIsDrawerOpen,
  setSelectedPothole,
  setIsPotholeModalOpen,
  is3DModelVisible, // Prop needed for click-off logic
  // Add props for closing panels
  handleClose3DModel,
  handleCloseInspectionPanel,
  show3DModel,
  showInspectionPanel,
  // Add props for current asset IDs
  current3DModelAssetId,
  currentInspectionAssetId,
  // Add footprint props
  isFootprintVisible,
  setIsFootprintVisible,
  setSelectedFootprintAsset, 
  currentFootprintAssetId
}) => {
  // Get viewerRef from context here
  const viewerRef = useCesiumViewer();

  // useEffect for setting up map interactions
  useEffect(() => {
    // Use the viewerRef prop
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer) return;

    const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

    // MOUSE_MOVE for tooltips
    handler.setInputAction((movement) => {
      const pickedObject = viewer.scene.pick(movement.endPosition);

      // Check if we picked an entity and it has assetData properties
      if (defined(pickedObject) && defined(pickedObject.id) && pickedObject.id.properties) {
        const properties = pickedObject.id.properties.getValue(viewer.clock.currentTime);
        if (properties && properties.assetData) { // Check if it's an asset entity
          const { id, name, category_name, subtype_name, network_name, section_name, type_name, slope_type, rating_name, rating_color_code } = properties.assetData;

          // Format rating display (convert Cx1 → Rx1)
          const displayRating = rating_name ? rating_name.replace(/^C/, 'R') : null;

          // Create a more structured tooltip content
          const tooltipContent = `
            <div style="font-weight:bold;margin-bottom:4px;">${name || `Asset ${id}`}</div>
            <div>Category: ${category_name || 'N/A'}</div>
            <div>Type: ${subtype_name || 'N/A'}</div>
            ${type_name === 'Slope' && slope_type ? `<div>Slope Type: ${slope_type === 'C' ? 'Cut' : 'Embankment'}</div>` : ''}
            <div>Network: ${network_name || 'N/A'}</div>
            ${section_name ? `<div>Section: ${section_name}</div>` : ''}
            ${displayRating ? `<div>Rating: <span style="background-color:${rating_color_code || '#888'};color:#fff;padding:1px 6px;border-radius:3px;font-size:10px;">${displayRating}</span></div>` : ''}
            <div style="font-style:italic;margin-top:4px;font-size:11px">Click for details</div>
          `;
          
          // Use state setters passed as props
          setTooltipContent(tooltipContent);
          setTooltipPosition({ x: movement.endPosition.x + 10, y: movement.endPosition.y - 10 });
          setTooltipVisible(true);
        } else {
          setTooltipVisible(false); // Not our asset entity
        }
      } else {
        setTooltipVisible(false); // Nothing picked or not an entity with properties
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);

    // LEFT_CLICK for selecting assets
    handler.setInputAction((click) => {
      const pickedObject = viewer.scene.pick(click.position);
      if (defined(pickedObject) && defined(pickedObject.id) && pickedObject.id.properties) {
        const properties = pickedObject.id.properties.getValue(viewer.clock.currentTime);
        if (properties && properties.assetData) {
          const clickedAssetId = properties.assetData.id;

          // Close 3D model ONLY if it's visible AND the clicked asset is DIFFERENT
          if (show3DModel && clickedAssetId !== current3DModelAssetId) {
            console.log(`InteractionHandler: Closing active 3D model (Asset ${current3DModelAssetId}) because different asset (Asset ${clickedAssetId}) was clicked.`);
            handleClose3DModel();
          }
          // Close inspection panel ONLY if it's visible AND the clicked asset is DIFFERENT
          if (showInspectionPanel && clickedAssetId !== currentInspectionAssetId) {
            console.log(`InteractionHandler: Closing active inspection panel (Asset ${currentInspectionAssetId}) because different asset (Asset ${clickedAssetId}) was clicked.`);
            handleCloseInspectionPanel();
          }
          
          // Handle footprint visibility when clicking on a different asset
          if (isFootprintVisible && clickedAssetId !== currentFootprintAssetId) {
            console.log(`InteractionHandler: Hiding footprint for Asset ${currentFootprintAssetId} because different asset (Asset ${clickedAssetId}) was clicked.`);
            setIsFootprintVisible(false);
            setSelectedFootprintAsset(null);
          }

          // Use state setters passed as props
          setSelectedAsset(properties.assetData);
          setIsDrawerOpen(true);
          setTooltipVisible(false); // Hide tooltip on click

          // Fly camera to the clicked asset
          const viewer = viewerRef.current?.cesiumElement;
          const targetEntity = pickedObject.id; // The entity itself
          if (viewer && targetEntity && targetEntity.position) {
            // Get the entity's current position
            const targetPosition = targetEntity.position.getValue(viewer.clock.currentTime);
            if (targetPosition) {
              // Create a small bounding sphere around the target position
              const boundingSphere = new BoundingSphere(targetPosition, 1.0); // 1m radius sphere
              // Define camera offset using imported modules
              const offset = new HeadingPitchRange(0, CesiumMath.toRadians(-45), 100); // Heading 0, Pitch -45 deg, Range 100m
              // Use flyToBoundingSphere for better framing
              viewer.camera.flyToBoundingSphere(boundingSphere, {
                offset: offset,
                duration: 1.5 // seconds
              });
            } else {
              console.warn("Could not get position for selected entity.");
            }
          }
        } else if (properties && properties.potholeData) { // Check for potholeData
          // Handle pothole click
          // Also close 3D model/inspection panel if a pothole is clicked
          if (show3DModel) {
            console.log("InteractionHandler: Closing active 3D model because pothole was clicked.");
            handleClose3DModel();
          }
          if (showInspectionPanel) {
            console.log("InteractionHandler: Closing active inspection panel because pothole was clicked.");
            handleCloseInspectionPanel();
          }
          // Hide footprint if visible
          if (isFootprintVisible) {
            console.log("InteractionHandler: Hiding footprint because pothole was clicked.");
            setIsFootprintVisible(false);
            setSelectedFootprintAsset(null);
          }

          const potholeData = properties.potholeData;
          setSelectedPothole(potholeData);
          setIsPotholeModalOpen(true);
          setTooltipVisible(false); // Hide tooltip on click
        } else {
          // Clicked on something else without relevant data (e.g., GeoJSON feature)
          setTooltipVisible(false); // Always hide tooltip
          // Close panels/model if click wasn't on an asset or pothole
          // (Unless the 3D model itself was clicked - handled by is3DModelVisible check later)
          if (!is3DModelVisible) {
              if (show3DModel) {
                 console.log("InteractionHandler: Closing active 3D model due to click on non-asset/pothole.");
                 handleClose3DModel();
              }
              if (showInspectionPanel) {
                  console.log("InteractionHandler: Closing active inspection panel due to click on non-asset/pothole.");
                  handleCloseInspectionPanel();
              }
              // Hide footprint if visible
              if (isFootprintVisible) {
                console.log("InteractionHandler: Hiding footprint due to click on non-asset/pothole.");
                setIsFootprintVisible(false);
                setSelectedFootprintAsset(null);
              }
              // Also clear selection if click is not on asset/pothole
              setSelectedAsset(null);
              setIsDrawerOpen(false);
              setSelectedPothole(null);
              setIsPotholeModalOpen(false);
          }
        }
      } else {
        // Clicked on empty space
        setTooltipVisible(false); // Always hide tooltip
        // Clear selections and close drawers/modals only if 3D model is NOT visible
        if (!is3DModelVisible) {
          if (show3DModel) {
            console.log("InteractionHandler: Closing active 3D model due to click on empty space.");
            handleClose3DModel();
          }
          if (showInspectionPanel) {
            console.log("InteractionHandler: Closing active inspection panel due to click on empty space.");
            handleCloseInspectionPanel();
          }
          // Hide footprint if visible
          if (isFootprintVisible) {
            console.log("InteractionHandler: Hiding footprint due to click on empty space.");
            setIsFootprintVisible(false);
            setSelectedFootprintAsset(null);
          }
          setSelectedAsset(null);
          setIsDrawerOpen(false);
          setSelectedPothole(null);
          setIsPotholeModalOpen(false);
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);

    // Cleanup function to remove the handler when component unmounts or dependencies change
    return () => {
      handler.destroy();
    };
    // Dependencies: viewerRef object from context, assets, and state setters
    // Dependency array includes assets, setters, 3D model/inspection visibility, close handlers, and viewerRef.
  }, [
      assets,
      setTooltipVisible, setTooltipContent, setTooltipPosition,
      setSelectedAsset, setIsDrawerOpen,
      setSelectedPothole, setIsPotholeModalOpen,
      is3DModelVisible,
      show3DModel, handleClose3DModel,
      showInspectionPanel, handleCloseInspectionPanel,
      current3DModelAssetId,
      currentInspectionAssetId,
      isFootprintVisible, setIsFootprintVisible, setSelectedFootprintAsset, currentFootprintAssetId,
      viewerRef
    ]);

  // This component doesn't render anything itself
  return null;
};

export default InteractionHandler;